import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { User, LoginRequest } from '../types/auth';
import { authService } from '../services/authService';
import { scheduleTokenRefresh } from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (request: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    const token = localStorage.getItem('admin-accessToken');
    if (token) {
      authService
        .getCurrentUser()
        .then((data) => setUser(data))
        .catch(() => {
          localStorage.removeItem('admin-accessToken');
          localStorage.removeItem('admin-refreshToken');
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (request: LoginRequest) => {
    const response = await authService.login(request);
    if (!response.user.isSystemAdmin) {
      throw new Error('Bu panele erişim yetkiniz bulunmamaktadır.');
    }
    localStorage.setItem('admin-accessToken', response.accessToken);
    localStorage.setItem('admin-refreshToken', response.refreshToken);
    scheduleTokenRefresh();
    setUser(response.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.removeItem('admin-accessToken');
      localStorage.removeItem('admin-refreshToken');
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
